import { useState, useEffect, useRef } from "react";
import { Car, Home, X, Users, Plus, Loader2, Tag } from "lucide-react";
import api from "../../services/api";

var COLS = 25;
var START = 10;
var END = 3000;

function QuotaCellPopover({ cota, internal, external, onClose, position }) {
  var clients = internal || [];
  var extBrokers = external || [];

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-72 max-h-80 overflow-y-auto"
        style={{ top: Math.min(position.y, window.innerHeight - 340), left: Math.min(position.x, window.innerWidth - 300) }}
        onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-[#0A2F3D]">Cota {cota}</h4>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        {clients.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-emerald-700 mb-1.5 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />Clientes Methos ({clients.length})
            </p>
            <div className="space-y-1.5">
              {clients.map(function(c, i) {
                return (
                  <a key={i} href={"/clientes/" + c.client_id} data-testid={"popover-client-" + i}
                    className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg border border-emerald-100 hover:bg-emerald-100 transition-colors block">
                    <div>
                      <p className="text-xs font-medium text-gray-800">{c.client_name}</p>
                      <p className="text-[10px] text-gray-500">Grupo {c.grupo || "-"} | {c.status}</p>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}
        {extBrokers.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-fuchsia-700 mb-1.5">Outra Corretora</p>
            {extBrokers.map(function(b, i) {
              return (
                <div key={i} className="p-2 bg-fuchsia-50 rounded-lg border border-fuchsia-100 text-xs text-fuchsia-800">{b}</div>
              );
            })}
          </div>
        )}
        {clients.length === 0 && extBrokers.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-2">Cota disponivel</p>
        )}
      </div>
    </div>
  );
}

function ExternalQuotaModal({ category, onClose, onSave }) {
  var cotasState = useState("");
  var brokerState = useState("");
  var loadingState = useState(false);

  async function handleSubmit() {
    var nums = cotasState[0].split(/[,;\s]+/).map(function(s) { return parseInt(s.trim()); }).filter(function(n) { return n >= 10 && n <= 3000; });
    if (nums.length === 0) return;
    loadingState[1](true);
    try {
      await api.post("/quota-map/external/bulk", {
        cotas: nums,
        category: category,
        broker_name: brokerState[0] || "Outra corretora",
      });
      onSave();
    } catch(e) { console.error(e); }
    finally { loadingState[1](false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={function(e) { e.stopPropagation(); }}>
        <h3 className="text-base font-bold text-[#0A2F3D] mb-4">Marcar Cotas Externas</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Nome da Corretora</label>
            <input type="text" value={brokerState[0]} onChange={function(e) { brokerState[1](e.target.value); }}
              placeholder="Ex: Porto Seguro Consorcios" data-testid="external-broker-input"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Numeros das Cotas (separados por virgula)</label>
            <textarea value={cotasState[0]} onChange={function(e) { cotasState[1](e.target.value); }}
              placeholder="Ex: 125, 485, 721, 965" rows={3} data-testid="external-cotas-input"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6] resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={loadingState[0]} data-testid="external-save-button"
            className="px-4 py-2 text-sm font-medium rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50 flex items-center gap-1.5">
            {loadingState[0] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Marcar
          </button>
        </div>
      </div>
    </div>
  );
}

function ClassifyBanner({ count, onClassify }) {
  var loadingState = useState(false);
  var resultState = useState(null);

  async function handleClassify() {
    loadingState[1](true);
    try {
      var res = await api.post("/quota-map/classify");
      resultState[1](res.data);
      setTimeout(function() { onClassify(); }, 1500);
    } catch(e) { console.error(e); }
    finally { loadingState[1](false); }
  }

  if (resultState[0]) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="classify-result">
        <Tag className="w-4 h-4 text-emerald-600" />
        <p className="text-xs text-emerald-800">
          <strong>{resultState[0].automovel}</strong> reclassificados como Automovel e <strong>{resultState[0].imovel}</strong> como Imovel (por grupo)
        </p>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between" data-testid="classify-banner">
      <div className="flex items-center gap-2">
        <Tag className="w-4 h-4 text-blue-600" />
        <p className="text-xs text-blue-800">
          Classificacao por grupo: grupo menor que 1.000 = Automovel, grupo 1.000 ou mais = Imovel.
          {count > 0 ? " (" + count + " sem categoria)" : ""}
        </p>
      </div>
      <button onClick={handleClassify} disabled={loadingState[0]} data-testid="classify-button"
        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
        {loadingState[0] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
        Reclassificar por Grupo
      </button>
    </div>
  );
}

export default function QuotaMap() {
  var categoryState = useState("automovel");
  var category = categoryState[0];
  var dataState = useState({ internal: {}, external: {}, unclassified_count: 0 });
  var data = dataState[0];
  var loadingState = useState(true);
  var popoverState = useState(null);
  var showExternalState = useState(false);
  var searchState = useState("");

  function fetchMap() {
    loadingState[1](true);
    api.get("/quota-map?category=" + category).then(function(res) {
      dataState[1](res.data);
    }).catch(function() {}).finally(function() { loadingState[1](false); });
  }

  useEffect(function() { fetchMap(); }, [category]);

  var numbers = [];
  for (var i = START; i <= END; i++) { numbers.push(i); }

  var scrollRef = useRef(null);

  function scrollToNumber(num) {
    if (!scrollRef.current) return;
    var el = scrollRef.current.querySelector('[data-cota="' + num + '"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-[#3B82F6]");
      setTimeout(function() { el.classList.remove("ring-4", "ring-[#3B82F6]"); }, 2000);
    }
  }

  function handleSearch() {
    var num = parseInt(searchState[0]);
    if (num >= 10 && num <= 3000) scrollToNumber(num);
  }

  function handleCellClick(cota, e) {
    var intl = data.internal[String(cota)] || [];
    var ext = data.external[String(cota)] || [];
    if (intl.length === 0 && ext.length === 0) return;
    var rect = e.currentTarget.getBoundingClientRect();
    popoverState[1]({ cota: cota, internal: intl, external: ext, position: { x: rect.left, y: rect.bottom + 4 } });
  }

  var internalCount = Object.keys(data.internal).length;
  var externalCount = Object.keys(data.external).length;

  return (
    <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden" data-testid="quota-map-container">
      <div className="p-4 lg:p-6 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Mapa de Cotas</h3>
            <p className="text-xs text-gray-500">Visualizacao das cotas 10 a 3.000 — conectado a sua base de clientes</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={function() { showExternalState[1](true); }} data-testid="add-external-button"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50">
              <Plus className="w-3.5 h-3.5" />Marcar Externa
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button onClick={function() { categoryState[1]("automovel"); }} data-testid="tab-automovel"
              className={"px-4 py-2 text-xs font-medium rounded-lg flex items-center gap-1.5 transition-all " + (category === "automovel" ? "bg-white text-[#0A2F3D] shadow-sm" : "text-gray-500 hover:text-gray-700")}>
              <Car className="w-4 h-4" />Automovel
            </button>
            <button onClick={function() { categoryState[1]("imovel"); }} data-testid="tab-imovel"
              className={"px-4 py-2 text-xs font-medium rounded-lg flex items-center gap-1.5 transition-all " + (category === "imovel" ? "bg-white text-[#0A2F3D] shadow-sm" : "text-gray-500 hover:text-gray-700")}>
              <Home className="w-4 h-4" />Imovel
            </button>
          </div>

          <div className="flex items-center gap-2 flex-1">
            <input type="number" min="10" max="3000" value={searchState[0]}
              onChange={function(e) { searchState[1](e.target.value); }}
              onKeyDown={function(e) { if (e.key === "Enter") handleSearch(); }}
              placeholder="Buscar cota..." data-testid="quota-search-input"
              className="w-32 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20" />
            <button onClick={handleSearch}
              className="px-3 py-2 text-xs font-medium rounded-xl bg-[#3B82F6] text-white hover:bg-[#2563EB]" data-testid="quota-search-button">Ir</button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gray-100 border border-gray-200" />
            <span className="text-[10px] text-gray-500">Disponivel</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-emerald-500" />
            <span className="text-[10px] text-gray-500">Methos ({internalCount})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-fuchsia-500" />
            <span className="text-[10px] text-gray-500">Outra Corretora ({externalCount})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-amber-400" />
            <span className="text-[10px] text-gray-500">Ambas</span>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6 pt-4">
        <ClassifyBanner count={data.unclassified_count} onClassify={fetchMap} />
      </div>

      {loadingState[0] ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div ref={scrollRef} className="p-3 lg:p-4 overflow-auto max-h-[70vh]" data-testid="quota-grid">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(" + COLS + ", minmax(0, 1fr))", gap: "2px" }}>
            {numbers.map(function(num) {
              var hasInternal = !!data.internal[String(num)];
              var hasExternal = !!data.external[String(num)];
              var hasBoth = hasInternal && hasExternal;
              var bgCls = "bg-gray-50 text-gray-400 border-gray-100";
              if (hasBoth) bgCls = "bg-amber-400 text-amber-900 border-amber-500 cursor-pointer hover:bg-amber-300";
              else if (hasInternal) bgCls = "bg-emerald-500 text-white border-emerald-600 cursor-pointer hover:bg-emerald-400";
              else if (hasExternal) bgCls = "bg-fuchsia-500 text-white border-fuchsia-600 cursor-pointer hover:bg-fuchsia-400";

              return (
                <div key={num} data-cota={num} data-testid={"cota-cell-" + num}
                  onClick={function(e) { handleCellClick(num, e); }}
                  className={"flex items-center justify-center border rounded text-[9px] lg:text-[10px] font-medium py-1 select-none transition-all " + bgCls}
                  title={"Cota " + num}>
                  {num}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {popoverState[0] && (
        <QuotaCellPopover
          cota={popoverState[0].cota}
          internal={popoverState[0].internal}
          external={popoverState[0].external}
          position={popoverState[0].position}
          onClose={function() { popoverState[1](null); }}
        />
      )}

      {showExternalState[0] && (
        <ExternalQuotaModal
          category={category}
          onClose={function() { showExternalState[1](false); }}
          onSave={function() { showExternalState[1](false); fetchMap(); }}
        />
      )}
    </div>
  );
}
